import { useRef, useState } from "react";
import { Navigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { apiClient } from "../lib/apiClient";
import {
  getBillingEntitlement,
  humanizePlanKey,
  useCreditBilling,
} from "../lib/billing";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { queryKeys } from "../lib/queryKeys";
import { checkoutPlanKey, parseCheckoutSearch } from "../lib/routerSearch";
import { InlineError, Skeleton, Stack, Text } from "../ui";
import { CheckoutPayment } from "./CheckoutPayment";
import { CheckoutPlanPicker } from "./CheckoutPlanPicker";
import { CheckoutStepper } from "./CheckoutStepper";

export function CheckoutScreen() {
  const capability = useCreditBilling();
  const search = useSearch({ strict: false });
  const [session, setSession] = useState<{
    clientSecret: string;
    planKey: string;
  } | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitting = useRef(false);
  const attempt = useRef(0);
  const entitlement = useQuery({
    enabled: capability.enabled,
    queryKey: queryKeys.billingEntitlement,
    queryFn: getBillingEntitlement,
    retry: false,
  });
  const planKey = checkoutPlanKey(
    parseCheckoutSearch(search),
    entitlement.data?.available_plan_keys ?? [],
  );
  async function continueToPayment() {
    if (submitting.current || !planKey) return;
    if (!capability.stripePublishableKey) {
      setError("Payment is not configured. Contact support to continue.");
      return;
    }
    submitting.current = true;
    setIsPending(true);
    setError(null);
    const currentAttempt = ++attempt.current;
    try {
      const { data, error: apiError } = await apiClient.POST(
        "/v1/billing/checkout:create-session",
        { body: { plan_key: planKey } },
      );
      if (apiError) throw new ApiRequestError(apiError);
      if (!data.client_secret.trim()) {
        setError("Payment could not start. Please try again.");
        return;
      }
      if (attempt.current === currentAttempt) {
        // Component memory only: never query cache, URL, logs or storage.
        setSession({ clientSecret: data.client_secret, planKey });
      }
    } catch (cause) {
      setError(requestErrorDescription(cause));
    } finally {
      submitting.current = false;
      setIsPending(false);
    }
  }
  if (capability.isError)
    return (
      <InlineError>Unable to load your account. Reload to retry.</InlineError>
    );
  if (capability.isPending)
    return <Skeleton shape="detail-field" label="Loading checkout" rows={4} />;
  if (!capability.enabled) return <Navigate replace to="/" />;
  if (entitlement.isPending)
    return <Skeleton shape="detail-field" label="Loading plans" rows={4} />;
  if (entitlement.isError)
    return <InlineError>{entitlement.error.message}</InlineError>;
  if (entitlement.data.billing_state === "active")
    return <Navigate replace to="/settings/billing" />;
  if (!entitlement.data.available_plan_keys.length) {
    return (
      <Stack gap={4}>
        <Text>No plans available</Text>
        <Text variant="muted">Check back shortly or contact support.</Text>
      </Stack>
    );
  }
  const selectedPlan = entitlement.data.plan_catalog.find(
    (plan) => plan.plan_key === session?.planKey,
  );
  return (
    <Stack gap={6}>
      <CheckoutStepper payment={session !== null} />
      {session && capability.stripePublishableKey ? (
        <Stack gap={4}>
          <div className="marker-checkout-selected-plan">
            <Text>
              {selectedPlan?.display_name || humanizePlanKey(session.planKey)}
            </Text>
            <button
              className="marker-checkout-text-link"
              type="button"
              onClick={() => {
                ++attempt.current;
                setSession(null);
                setError(null);
              }}
            >
              Change
            </button>
          </div>
          <CheckoutPayment
            clientSecret={session.clientSecret}
            publishableKey={capability.stripePublishableKey}
          />
        </Stack>
      ) : (
        <CheckoutPlanPicker
          entitlement={entitlement.data}
          selectedPlanKey={planKey}
          isPending={isPending}
          error={error}
          onConfirm={() => {
            void continueToPayment();
          }}
        />
      )}
    </Stack>
  );
}
